<template>
	<transition
		v-if="campaignEnabled"
		enter-active-class="transition-all duration-300 ease-out"
		enter-from-class="-translate-y-full opacity-0"
		enter-to-class="translate-y-0 opacity-100"
		leave-active-class="transition-all duration-200 ease-in"
		leave-from-class="translate-y-0 opacity-100"
		leave-to-class="-translate-y-full opacity-0"
	>
		<div
			v-if="visible"
			class="fw-announcement-bar sticky top-0 inset-x-0 z-[55] bg-[#2f3f41] text-white shadow-md print:hidden"
			role="region"
			aria-label="Free Worship Together starter kit"
		>
			<a
				:href="kitUrl"
				@click="trackClick"
				class="block relative px-10 sm:px-12 py-2 sm:py-2.5 group"
			>
				<div class="max-w-6xl mx-auto flex items-center justify-center gap-2 sm:gap-3 text-xs sm:text-sm leading-snug">
					<span class="font-serif italic text-[#dcae63] tracking-tight whitespace-nowrap hidden sm:inline">Go. Gather. Worship.</span>
					<span class="hidden sm:inline text-white/25">&middot;</span>
					<span class="font-medium truncate">
						<span class="sm:hidden">Free &ldquo;Worship Together&rdquo; starter kit</span>
						<span class="hidden sm:inline">Get the free &ldquo;Worship Together&rdquo; starter kit</span>
					</span>
					<i class="fas fa-arrow-right text-[10px] sm:text-xs text-[#dcae63] transition-transform group-hover:translate-x-0.5 flex-shrink-0" />
				</div>
			</a>
			<button
				@click="dismiss"
				class="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-white/60 hover:text-white print:hidden"
				aria-label="Dismiss announcement"
			>
				<i class="fal fa-times text-xs sm:text-sm" />
			</button>
		</div>
	</transition>
</template>

<script>
import createCampaignElementMixin from '../../helpers/campaignElementMixin'

const KIT_PATH = '/worship-together'
const UTM = 'utm_source=cfl-site&utm_medium=top-announcement-bar&utm_campaign=worship-together'
const DISMISS_KEY = 'fwKitTopBarDismissed'
const CONVERTED_KEY = 'fwKitConverted'
const BODY_CLASS = 'has-fw-announcement-bar'
const EXCLUDED_PATTERNS = [/^\/(es|cn)(\/|$|\?)/i, /^\/worship-together(\/|$|\?)/i, /^\/restore-family-worship(\/|$|\?)/i, /checkout/i, /starter-kit/i]

export default {
	mixins: [createCampaignElementMixin('top-announcement-bar')],
	data() {
		return {
			visible: false,
			impressionTracked: false,
		}
	},
	computed: {
		kitUrl() {
			return `${KIT_PATH}?${UTM}`
		},
		navHidden() {
			return !!this.$route?.query?.navHidden
		},
		onExcludedRoute() {
			const path = this.$route?.path || ''
			return EXCLUDED_PATTERNS.some(re => re.test(path))
		},
	},
	mounted() {
		this.evaluateVisible()
	},
	beforeUnmount() {
		this.applyBodyClass(false)
	},
	watch: {
		visible(v) {
			this.applyBodyClass(v)
		},
		// campaignEnabled (from the mixin) flips when the campaign is toggled in
		// admin or when navigating into/out of an embed route. It must gate the
		// body class too: previously `mounted()` added `has-fw-announcement-bar`
		// unconditionally, so when the campaign was OFF the bar's `v-if` hid it but
		// the body class lingered — reserving a ~2.25rem offset above the nav with
		// nothing in it (the "gap above the nav bar"). Re-evaluate on every change.
		campaignEnabled() {
			this.evaluateVisible()
		},
		navHidden() {
			this.evaluateVisible()
		},
	},
	methods: {
		// Single source of truth for whether the bar shows AND whether the body
		// class is applied — so the nav offset is only ever reserved when the bar
		// is genuinely rendered.
		evaluateVisible() {
			// Admin artwork preview: always render, skip suppression/nav gating.
			if (this.preview) {
				this.visible = true
				return
			}
			let suppressed = false
			try {
				suppressed =
					window.sessionStorage.getItem(DISMISS_KEY) === '1' ||
					window.sessionStorage.getItem(CONVERTED_KEY) === '1'
			} catch {}
			const show = this.campaignEnabled && !this.navHidden && !suppressed
			this.visible = show
			if (show && !this.impressionTracked) {
				this.impressionTracked = true
				this.trackImpression()
			}
		},
		applyBodyClass(on) {
			try {
				if (typeof document === 'undefined') return
				if (on) document.body.classList.add(BODY_CLASS)
				else document.body.classList.remove(BODY_CLASS)
			} catch {}
		},
		dismiss() {
			this.visible = false
			try { window.sessionStorage.setItem(DISMISS_KEY, '1') } catch {}
			this.trackCampaignDismiss()
		},
		trackImpression() {
			this.trackCampaignImpression()
			try {
				if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
					window.gtag('event', 'family_worship_kit_top_bar_impression', {
						event_category: 'engagement',
						event_label: 'top-announcement-bar',
						route: this.$route?.path,
					})
				}
			} catch {}
		},
		trackClick() {
			this.trackCampaignClick({ link_url: this.kitUrl })
			try {
				if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
					window.gtag('event', 'family_worship_kit_top_bar_click', {
						event_category: 'engagement',
						event_label: 'top-announcement-bar',
						link_url: this.kitUrl,
						route: this.$route?.path,
					})
				}
			} catch {}
			try {
				window.sessionStorage.setItem(CONVERTED_KEY, '1')
			} catch {}
		},
	},
}
</script>
